/**
 * Empty Cart Enhanced - Recently Viewed Products
 * Handles displaying recently viewed products from localStorage
 */

(function() {
  'use strict';
  
  const STORAGE_KEY = 'recently_viewed_products';
  const MAX_PRODUCTS = 12;
  
  class EmptyCartRecentlyViewed {
    constructor() {
      this.section = document.querySelector('[data-recently-viewed-section]');
      this.container = document.querySelector('[data-recently-viewed-container]');
      this.grid = document.querySelector('[data-recently-viewed-grid]');
      
      if (!this.section || !this.container || !this.grid) {
        return; // Section not present, exit
      }
      
      this.maxProducts = parseInt(this.container.dataset.maxProducts) || MAX_PRODUCTS;
      this.init();
    }
    
    init() {
      const products = this.getRecentlyViewed();
      
      if (products.length === 0) {
        this.hideSection();
        return;
      }
      
      this.renderProducts(products);
    }
    
    getRecentlyViewed() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) return [];
        
        const products = JSON.parse(stored);
        if (!Array.isArray(products)) return [];
        
        // Remove duplicates and limit
        const uniqueProducts = [...new Set(products)];
        return uniqueProducts.slice(0, this.maxProducts);
      } catch (e) {
        // console.warn('EmptyCart: Could not access localStorage', e);
        return [];
      }
    }
    
    hideSection() {
      if (this.section) {
        this.section.style.display = 'none';
      }
    }
    
    async renderProducts(productHandles) {
      if (!productHandles || productHandles.length === 0) {
        this.hideSection();
        return;
      }
      
      try {
        // Show loading state
        this.grid.innerHTML = '<div style="grid-column: 1/-1; text-align: center; padding: 2rem; color: #666;">Loading...</div>';
        
        // Fetch all products in parallel
        const productPromises = productHandles.map(handle => this.fetchProduct(handle));
        const products = await Promise.allSettled(productPromises);
        
        // Filter successful results
        const validProducts = products
          .filter(result => result.status === 'fulfilled' && result.value)
          .map(result => result.value);
        
        if (validProducts.length === 0) {
          this.hideSection();
          return;
        }
        
        // Render products
        this.grid.innerHTML = validProducts.map(product => this.renderProductCard(product)).join('');
        
        // Mark section as having products
        this.section.setAttribute('data-has-products', 'true');
        this.section.style.display = 'block';
        
      } catch (error) {
        // console.error('EmptyCart: Error rendering recently viewed products', error);
        this.hideSection();
      }
    }
    
    async fetchProduct(handle) {
      try {
        const response = await fetch(`/products/${handle}.js`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return await response.json();
      } catch (error) {
        // console.warn(`EmptyCart: Could not fetch product ${handle}`, error);
        return null;
      }
    }
    
    renderProductCard(product) {
      if (!product) return '';
      
      const image = product.featured_image || product.images?.[0];
      const imageUrl = image ? this.getImageUrl(image, 400) : '';
      const imageAlt = image?.alt || product.title || '';
      
      const price = this.formatPrice(product.price);
      const comparePrice = product.compare_at_price > product.price 
        ? this.formatPrice(product.compare_at_price) 
        : null;
      
      const url = `/products/${product.handle}`;
      const available = product.available !== false;
      
      return `
        <div class="empty-cart-enhanced__recently-viewed-product">
          <div class="card-product bg-white relative overflow-hidden rounded-md lg:rounded-[15px] drop-shadow-sm lg:drop-shadow-md">
            <div class="card-product--inner w-full">
              <div class="card-product--top w-full relative">
                <a href="${url}" class="block relative overflow-hidden aspect-[4/4]">
                  ${imageUrl ? `
                    <img 
                      src="${imageUrl}" 
                      alt="${this.escapeHtml(imageAlt)}" 
                      loading="lazy"
                      class="w-full h-full object-contain"
                    />
                  ` : `
                    <div class="w-full h-full flex items-center justify-center bg-gray-100">
                      <svg width="64" height="64" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style="opacity: 0.3;">
                        <path d="M0 0.909091C0 0.407014 0.407014 0 0.909091 0H3.13557C3.68784 0 4.16996 0.374119 4.30709 0.909091C4.44423 1.44406 4.92634 1.81818 5.47861 1.81818H17.277C18.6271 1.81818 19.5893 3.12826 19.1854 4.41648L17.3044 10.4165C17.0429 11.2505 16.2701 11.8182 15.396 11.8182H7.37337C6.95146 11.8182 6.585 12.1085 6.48842 12.5192C6.35422 13.0898 6.78718 13.6364 7.37337 13.6364H17.9048C18.4069 13.6364 18.8139 14.0434 18.8139 14.5455C18.8139 15.0475 18.4069 15.4545 17.9048 15.4545H6.56076C5.27134 15.4545 4.31887 14.2524 4.61377 12.9971L4.98959 11.3975C5.06342 11.0832 5.06008 10.7558 4.97987 10.4431L3.06777 2.98929C2.89097 2.3001 2.26982 1.81818 1.55832 1.81818H0.909091C0.407014 1.81818 0 1.41117 0 0.909091Z" fill="currentColor"/>
                      </svg>
                    </div>
                  `}
                </a>
                
                ${!available ? `
                  <div class="card-product--badges absolute bottom-2 left-2 flex">
                    <span class="badge flex px-2 py-1 rounded-[25px] bg-gray-500 text-white uppercase text-xs font-bold leading-none">
                      Sold Out
                    </span>
                  </div>
                ` : comparePrice ? `
                  <div class="card-product--badges absolute bottom-2 left-2 flex">
                    <span class="badge flex px-2 py-1 rounded-[25px] bg-rose-500 text-white uppercase text-xs font-bold leading-none">
                      On Sale
                    </span>
                  </div>
                ` : ''}
              </div>
              
              <div class="card-product--bottom px-4 pt-3">
                <h3 class="card-product--title text-sm font-semibold mb-2 line-clamp-2" style="min-height: 2.5rem;">
                  <a href="${url}" class="text-black hover:text-orange">
                    ${this.escapeHtml(product.title)}
                  </a>
                </h3>
                
                <div class="card-product--price mb-3">
                  ${comparePrice ? `
                    <span class="text-rose-500 font-bold">${price}</span>
                    <span class="text-gray-500 line-through ml-2">${comparePrice}</span>
                  ` : `
                    <span class="text-black font-bold">${price}</span>
                  `}
                </div>
                
                <div class="card-product--action pb-4">
                  <a href="${url}" class="bg-orange border-2 border-orange text-sm text-center font-bold text-white px-5 py-2 rounded-[25px] hover:bg-transparent hover:text-orange ease-in-out duration-300 w-full block text-center">
                    View Product
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;
    }
    
    getImageUrl(image, size) {
      if (!image) return '';
      if (typeof image === 'string') return image;
      return image.src || image.url || '';
    }
    
    formatPrice(cents) {
      if (!cents) return '£0.00';
      const pounds = (cents / 100).toFixed(2);
      return `£${pounds}`;
    }
    
    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
  }
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new EmptyCartRecentlyViewed();
    });
  } else {
    new EmptyCartRecentlyViewed();
  }
  
  // Re-initialize on section load (for theme editor)
  document.addEventListener('shopify:section:load', (event) => {
    if (event.detail.sectionId && document.querySelector(`[data-section-id="${event.detail.sectionId}"]`)) {
      new EmptyCartRecentlyViewed();
    }
  });
})();

